// mission-store-open-recovery.ts
//
// Tách THUẦN từ mission-store.ts (phase 10, lô A): logic quyết định trạng thái
// READY/RECOVERING khi `MissionStore.open()` chạy — scan journal, chạy recovery
// (partial_tail / pending manifest), so projection với journal. `mission-store.ts`
// gọi hàm này rồi dựng `MissionStore` (constructor private, phải ở lại đó).

import type { EventJournal } from "./event-journal.js";
import type { JournalScanReport } from "./journal-scan-report.js";
import { scanJournal } from "./journal-scan-report.js";
import { readProjection, replayRecords } from "./mission-projection-io.js";
import { emptyMissionState, type MissionState } from "./mission-state-reducer.js";
import {
  RecoveryBlockedError,
  type RecoveryDeps,
  type RecoveryManifest,
  reconcilePartialTail,
  resumePendingRecovery,
} from "./recovery-reconciler.js";

export type MissionStoreOpenOutcome =
  | { status: "RECOVERING"; state: MissionState; reason: string }
  | {
      status: "READY";
      state: MissionState;
      replayedFrom: number;
      /** Projection file tồn tại lúc mở (quyết định ghi lại projection ngay sau replay). */
      hadExistingProjection: boolean;
    };

/** The one `journal_recovered` event a completed recovery appends (shared by both call paths). */
export async function appendRecoveredEvent(
  journal: EventJournal,
  manifest: RecoveryManifest,
): Promise<void> {
  await journal.append([
    {
      event_id: `journal-recovered-${manifest.lastSeq}-${manifest.offset}`,
      operation_id: `recovery-${manifest.lastSeq}-${manifest.offset}`,
      delta: {
        kind: "journal_recovered",
        truncated_at_offset: manifest.offset,
        tail_bytes: manifest.tailBytes,
        tail_sha256: manifest.sha256,
        tail_file: manifest.tailFile,
      },
    },
  ]);
}

async function recoverTail(
  journalPath: string,
  scan: JournalScanReport,
  journal: EventJournal,
  deps: RecoveryDeps | undefined,
): Promise<JournalScanReport> {
  await reconcilePartialTail(
    journalPath,
    scan,
    (manifest) => appendRecoveredEvent(journal, manifest),
    deps,
  );
  return scanJournal(journalPath);
}

/**
 * Quyết định trạng thái mở store (không tạo `MissionStore` — constructor private
 * ở lại `mission-store.ts`, gọi bằng kết quả trả về ở đây). Giữ nguyên thứ tự
 * nhánh gốc: corrupt → đọc projection → partial_tail (recoverTail) hoặc
 * resumePendingRecovery → so projection/journal → replay.
 */
export async function resolveMissionStoreOpenOutcome(
  journalPath: string,
  projectionPath: string,
  journal: EventJournal,
  scan: JournalScanReport,
  recovery: RecoveryDeps | undefined,
): Promise<MissionStoreOpenOutcome> {
  let effective: JournalScanReport = scan;
  if (scan.status === "corrupt") {
    const reason =
      `Journal hỏng tại offset ${scan.problem?.offset} (${scan.problem?.kind}` +
      `${scan.problem?.seq !== undefined ? `, seq ${scan.problem.seq}` : ""}). ` +
      "Record đủ newline mà sai ⇒ KHÔNG skip; cần người xem xét.";
    return { status: "RECOVERING", state: emptyMissionState(), reason };
  }

  // Audit 2026-09-14 (M1): the projection is read BEFORE recovery now.
  // Previously `recoverTail` ran first and cut the unterminated tail
  // unconditionally — including a tail the projection had already counted,
  // which is durable evidence that the record's content WAS accepted. The
  // audit measured exactly that: seq 2 `done` lost its newline, the
  // read-only store said RECOVERING, and this path truncated seq 2 and
  // reported READY with the ticket rewound to `todo`.
  const projection = await readProjection(projectionPath);

  if (scan.status === "partial_tail") {
    try {
      effective = await recoverTail(journalPath, scan, journal, {
        ...recovery,
        // The production supplier `tailWasAcknowledged` never had. A
        // projection that already counts PAST the journal's last complete
        // record can only have got there by accepting the very record whose
        // tail is now unterminated.
        tailWasAcknowledged:
          recovery?.tailWasAcknowledged ??
          (async (report) =>
            projection !== undefined && projection.last_event_seq > report.lastSeq),
      });
    } catch (err) {
      // B7: `reconcilePartialTail` signals "refuse to cut" by throwing
      // RecoveryBlockedError. Letting it propagate would crash `open()`
      // instead of producing the RECOVERING store the caller can inspect.
      if (!(err instanceof RecoveryBlockedError)) throw err;
      return {
        status: "RECOVERING",
        state: projection ?? emptyMissionState(),
        reason: err.message,
      };
    }
  } else {
    // M2: a recovery interrupted after PHASE 2 leaves a CLEAN-scanning
    // journal, so the branch above never runs and the manifest would sit
    // pending forever. `truncated` is completed here; `saved` is handed to
    // a human (D3) rather than guessed at.
    const resumed = await resumePendingRecovery(
      journalPath,
      (manifest) => appendRecoveredEvent(journal, manifest),
      recovery,
    );
    if (resumed.kind === "blocked") {
      return {
        status: "RECOVERING",
        state: projection ?? emptyMissionState(),
        reason: resumed.reason,
      };
    }
    if (resumed.kind === "completed") effective = await scanJournal(journalPath);
  }

  if (projection && projection.last_event_seq > effective.lastSeq) {
    const reason =
      `Projection đi TRƯỚC journal (projection seq ${projection.last_event_seq} > ` +
      `journal seq ${effective.lastSeq}) — bằng chứng mất dữ liệu, phải điều tra.`;
    return { status: "RECOVERING", state: projection, reason };
  }

  const base = projection ?? emptyMissionState();
  const replayFrom = base.last_event_seq + 1;
  const state = replayRecords(base, effective.records, replayFrom);
  return {
    status: "READY",
    state,
    replayedFrom: replayFrom,
    hadExistingProjection: projection !== undefined,
  };
}
